// ReactPicky.js
import React, { useState, useEffect, useMemo, useRef } from "react";

import { LoadingSpinner } from "../Loaders";
import { CloseIcon } from "../SvgIcons";

const TAG_STYLE = {
	background: "#e6f4ff",
	border: "1px solid #b5dfff",
	borderRadius: 16,
	padding: "2px 10px",
	marginRight: 4,
	display: "flex",
	alignItems: "center",
	fontSize: 14,
	justifyContent: "center",
};

const CLOSE_X_STYLE = {
	marginLeft: 4,
	cursor: "pointer",
	fontWeight: "bold",
	color: "#666",
	fontSize: 16,
};

export const Picky = ({
	options = [],
	value = [],
	multiple = false,
	onChange,
	placeholder = "Select...",
	// Support both our name and react-picky's includeFilter/includeSelectAll
	enableFiltering,
	includeFilter = false,
	selectAll,
	includeSelectAll,
	filterPlaceholder = "Filter options...",
	labelKey = "label",
	valueKey = "value",
	className = "",
	id,
	dropdownHeight = 220,
	keepOpen = false,
	disabled = false,
	render,
	// Custom renderer for the selected value shown in the input trigger (single mode only)
	renderValue,
	// Limit how many selected values are visibly displayed (multi only)
	maxValuesDisplayed = 3,
	numberDisplayed,
	overflowFormatter,
	// Misc compatibility niceties
	selectAllText = "Select All",
	clearFilterOnClose = false,
	// NEW: allow hiding the global "Clear All" and single clear X
	allowClearAll = false,
	// Control dropdown placement explicitly: 'down' | 'up'
	dropdownPosition = "down",
	// Loading state support
	loading = false,
	loadingMessage = "Loading...",
	// Callback when dropdown is closed
	onClose,
}) => {
	// Normalize prop aliases
	const filteringEnabled =
		(enableFiltering ?? includeFilter ?? true) && !disabled;
	const showSelectAll =
		(selectAll ?? includeSelectAll ?? false) && multiple && options.length > 0;
	const [filter, setFilter] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	// Derive placement from prop
	const dropUp = dropdownPosition === "up";
	// Helpers to get label/value from arbitrary keys
	const getLabel = (opt) => (opt ? opt[labelKey] : undefined);
	const getValue = (opt) => (opt ? opt[valueKey] : undefined);

	// Normalize incoming "value" prop to an array of option objects for internal state
	const deriveSelectedFromValue = (val) => {
		if (!multiple) {
			if (!val) return [];
			// If primitive, find matching option
			if (typeof val !== "object") {
				const match = options.find((o) => getValue(o) === val);
				return match ? [match] : [];
			}
			// If object, ensure it maps to an existing option to keep referential equality
			const match = options.find((o) => getValue(o) === getValue(val));
			return match ? [match] : [];
		}
		// Multiple
		const arr = Array.isArray(val) ? val : [];
		// If primitives array, map to option objects
		if (arr.length > 0 && typeof arr[0] !== "object") {
			return arr
				.map((v) => options.find((o) => getValue(o) === v))
				.filter(Boolean);
		}
		// If objects array, map each to the option from options to preserve reference
		return arr
			.map((obj) => options.find((o) => getValue(o) === getValue(obj)))
			.filter(Boolean);
	};

	const [selected, setSelected] = useState(deriveSelectedFromValue(value));
	const containerRef = useRef();

	// Close dropdown on outside click.
	// Uses mousedown + capture so it fires before React's synthetic handlers
	// and cannot be stopped by inner elements calling e.stopPropagation().
	useEffect(() => {
		function handleOutsideMouseDown(e) {
			if (!containerRef.current?.contains(e.target)) {
				setIsOpen(false);
				if (typeof onClose === "function") {
					onClose();
				}
			}
		}
		if (isOpen) {
			document.addEventListener("mousedown", handleOutsideMouseDown, true);
		}
		return () => {
			document.removeEventListener("mousedown", handleOutsideMouseDown, true);
		};
	}, [isOpen, onClose]);

	// Sync selection with value prop
	useEffect(() => {
		setSelected(deriveSelectedFromValue(value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, multiple, options, labelKey, valueKey]);

	// Optionally clear filter when dropdown closes
	useEffect(() => {
		if (!isOpen && clearFilterOnClose) {
			setFilter("");
		}
	}, [isOpen, clearFilterOnClose]);

	const filteredOptions = useMemo(() => {
		if (!filteringEnabled || !filter.trim()) return options;
		return options.filter((opt) =>
			String(getLabel(opt) ?? "")
				.toLowerCase()
				.includes(filter.toLowerCase())
		);
	}, [filter, options, filteringEnabled, labelKey, valueKey]);

	const allSelected = useMemo(() => {
		if (!multiple) return false;
		if (options.length === 0) return false;
		if (selected.length !== options.length) return false;
		const selSet = new Set(selected.map((s) => getValue(s)));
		return options.every((o) => selSet.has(getValue(o)));
	}, [multiple, selected, options, labelKey, valueKey]);

	function toggleOption(option) {
		if (disabled || loading) return;
		if (multiple) {
			const isSelected = selected.some(
				(sel) => getValue(sel) === getValue(option)
			);
			const newSelected = isSelected
				? selected.filter((sel) => getValue(sel) !== getValue(option))
				: [...selected, option];
			setSelected(newSelected);
			onChange?.(newSelected);
		} else {
			setSelected([option]);
			onChange?.(option);
			if (!keepOpen) {
				setIsOpen(false);
				onClose?.();
			}
		}
	}

	function toggleSelectAll() {
		if (disabled || loading) return;
		if (allSelected) {
			setSelected([]);
			onChange?.([]);
		} else {
			setSelected(options);
			onChange?.(options);
		}
	}

	function handleClearAll(e) {
		e.stopPropagation();
		setSelected([]);
		onChange?.(multiple ? [] : null);
	}

	// For keyboard accessibility: Open dropdown with enter/space, close with Escape
	function handleKeyDown(ev) {
		if (ev.key === "Enter" || ev.key === " ") {
			setIsOpen((o) => !o);
		} else if (ev.key === "Escape" && isOpen) {
			closeDropdown();
		}
	}

	// Function to close the dropdown
	function closeDropdown() {
		if (isOpen) {
			setIsOpen(false);
			onClose?.();
		}
	}

	// Renders selected tags for multi, or label + x for single
	function renderSelected() {
		if (selected.length === 0) {
			return <span style={{ color: "#aaa" }}>{placeholder}</span>;
		}
		if (multiple) {
			const rawMax = maxValuesDisplayed ?? numberDisplayed;
			const maxCount = Number.isFinite(Number(rawMax))
				? Math.max(0, Number(rawMax))
				: Infinity;
			const showAll = !Number.isFinite(Number(rawMax));
			const visibleItems = showAll
				? selected
				: selected.slice(0, Math.min(selected.length, maxCount));
			const remaining = selected.length - visibleItems.length;
			return (
				<>
					{visibleItems.map((item, idx) => (
						<span
							key={`${getValue(item) ?? getLabel(item) ?? "item"}-${idx}`}
							style={TAG_STYLE}
							className="picky-tag">
							<span className="picky-label">{getLabel(item)}</span>
							<span
								className="picky-close"
								style={CLOSE_X_STYLE}
								tabIndex={0}
								role="button"
								aria-label={`Remove ${getLabel(item)}`}
								onClick={(e) => {
									e.stopPropagation();
									toggleOption(item);
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.stopPropagation();
										toggleOption(item);
									}
								}}>
								×
							</span>
						</span>
					))}
					{remaining > 0 && (
						<span
							style={TAG_STYLE}
							aria-label={`${remaining} more selected`}
							title={selected
								.slice(visibleItems.length)
								.map((it) => String(getLabel(it) ?? getValue(it) ?? ""))
								.join(", ")}>
							{typeof overflowFormatter === "function"
								? overflowFormatter(Math.min(remaining, 99))
								: `+${Math.min(remaining, 99)}${remaining > 99 ? "+" : ""}`}
						</span>
					)}
					{/* Clear-all button moved to right edge of input */}
				</>
			);
		}
		// Single value mode
		return (
			<>
				{typeof renderValue === "function" ? (
					renderValue(selected[0])
				) : (
					<span className="picky-label" style={{ marginRight: 16 }}>
						{getLabel(selected[0])}
					</span>
				)}
				{/* Clear button moved to right edge of input */}
			</>
		);
	}

	// Main Render
	return (
		<div
			className={`react-picky-container${
				disabled || loading ? " disabled" : ""
			} ${dropUp ? "react-picky-up" : "react-picky-down"} ${className}`}
			style={{
				position: "relative",
			}}
			ref={containerRef}
			id={id}>
			<div
				className="react-picky-input"
				tabIndex={0}
				onClick={() => !disabled && !loading && setIsOpen((prev) => !prev)}
				onKeyDown={handleKeyDown}
				style={{
					border: "1px solid #ccc",
					// Use side-specific paddings to avoid mixing with paddingRight
					paddingTop: selected.length === 0 ? 8 : 6,
					paddingBottom: selected.length === 0 ? 8 : 6,
					paddingLeft: 8,
					paddingRight:
						allowClearAll && selected.length > 0 && !disabled && !loading
							? 44
							: 8,
					borderRadius: 4,
					minHeight: 35,
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 3,
					cursor: disabled || loading ? "not-allowed" : "pointer",
					userSelect: "none",
					background: disabled || loading ? "#f7f7f7" : "#fff",
					position: "relative",
				}}
				aria-expanded={isOpen}
				aria-haspopup="listbox">
				{renderSelected()}
				{/* Right-aligned clear button — styled like fe-search-clear in TextInput */}
				{allowClearAll && selected.length > 0 && !disabled && !loading && (
					<button
						type="button"
						className="fe-search-clear picky-clear-btn"
						onClick={handleClearAll}
						aria-label="Clear selection">
						<CloseIcon size={12} />
					</button>
				)}
				{loading && (
					<span style={{ marginLeft: "auto" }}>
						<LoadingSpinner size="sm" center />
					</span>
				)}
			</div>

			{isOpen && (
				<div
					className={`react-picky-dropdown ${dropUp ? "drop-up" : "drop-down"}`}
					style={{
						position: "absolute",
						[dropUp ? "bottom" : "top"]: "100%",
						left: 0,
						right: 0,
						maxHeight: dropdownHeight,
						overflowY: "auto",
						border: "1px solid #ccc",
						borderTop: dropUp ? "1px solid #ccc" : "none",
						borderBottom: dropUp ? "none" : "1px solid #ccc",
						backgroundColor: "#fff",
						zIndex: 1000,
						boxShadow: dropUp
							? "0 -3px 12px 0 rgba(50,50,80,0.11)"
							: "0 3px 12px 0 rgba(50,50,80,0.11)",
					}}>
					{!loading && filteringEnabled && (
						<input
							type="text"
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
							placeholder={filterPlaceholder}
							style={{
								boxSizing: "border-box",
								width: "100%",
								padding: "8px",
								borderBottom: "1px solid #ddd",
								outline: 0,
								fontSize: 15,
							}}
							autoFocus
						/>
					)}

					{!loading && showSelectAll && (
						<div
							className="react-picky-select-all"
							style={{
								padding: "8px 12px",
								borderBottom: "1px solid #eee",
								cursor: "pointer",
								background: allSelected ? "#e6f7ff" : undefined,
								display: "flex",
								alignItems: "center",
							}}
							onClick={toggleSelectAll}
							role="option"
							aria-selected={allSelected}>
							<input
								type="checkbox"
								readOnly
								checked={allSelected}
								style={{ marginRight: 8 }}
								tabIndex={-1}
							/>
							{selectAllText}
						</div>
					)}

					{loading ? (
						<div style={{ padding: 16 }}>
							<LoadingSpinner size="sm" message={loadingMessage} center />
						</div>
					) : filteredOptions.length === 0 ? (
						<div
							style={{ padding: "12px", color: "#999", textAlign: "center" }}>
							No options found
						</div>
					) : (
						filteredOptions.map((option, idx) => {
							const isSelected = selected.some(
								(sel) => getValue(sel) === getValue(option)
							);
							return (
								<div
									key={`${
										getValue(option) ?? getLabel(option) ?? "opt"
									}-${idx}`}
									className={`react-picky-option${
										isSelected ? " selected" : ""
									}`}
									style={{
										padding: "9px 12px",
										cursor: disabled ? "not-allowed" : "pointer",
										background: isSelected ? "#bae7ff" : undefined,
										display: "flex",
										alignItems: "center",
										fontWeight: isSelected ? "bold" : "normal",
									}}
									onClick={(e) => {
										e.stopPropagation();
										toggleOption(option);
									}}
									role="option"
									aria-selected={isSelected}>
									{multiple && (
										<input
											type="checkbox"
											readOnly
											checked={isSelected}
											style={{ marginRight: 10 }}
											tabIndex={-1}
										/>
									)}
									{typeof render === "function"
										? // Custom item renderer compatibility with react-picky
										  render({
												style: {},
												item: option,
												isSelected,
												selectValue: toggleOption,
												labelKey,
												valueKey,
												// Provide context similar to react-picky
												// Not all consumers use this, but it's harmless
												context: "option",
										  })
										: getLabel(option)}
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};

export default Picky;
